/**
 * Bleed Left Image
 *
 * Calculates the exact left offset of .is-style-bleed-left elements
 * and their parent column width, then sets CSS variables so the image
 * bleeds to the left edge while its right edge stays pinned to the
 * column boundary.
 *
 * Safety: only activates when the image is inside the first column of
 * a columns block. If applied elsewhere, the style is ignored and a
 * console warning is logged.
 *
 * Loaded via enqueue_block_assets so it runs in both the front-end
 * and inside the block editor iframe.
 *
 * --bleed-offset:       negative px value to pull image left
 * --bleed-column-width: the parent column's width in px
 */

const FIGURE_SELECTOR = '.is-style-bleed-left';
const COLUMN_SELECTOR = '.wp-block-column';
const COLUMNS_SELECTOR = '.wp-block-columns';
const DEFAULT_WIDE_WIDTH = 1440;
const CONTEXT_WARNING =
	'Bleed Left style requires the image to be in the first column of a columns block.';

let resizeObserver = null;
let pendingFrame = null;
const observedColumns = new WeakSet();

function getWideWidth() {
	const value = getComputedStyle( document.documentElement )
		.getPropertyValue( '--wp--style--global--wide-size' )
		.trim();

	if ( value ) {
		return parseInt( value, 10 ) || DEFAULT_WIDE_WIDTH;
	}

	return DEFAULT_WIDE_WIDTH;
}

/**
 * Check if an element is inside the first column of a columns block.
 *
 * @param {Element} el
 * @return {Element|null} The column element, or null if invalid context.
 */
function getValidColumn( el ) {
	const column = el.closest( COLUMN_SELECTOR );
	if ( ! column ) {
		return null;
	}

	const columns = column.closest( COLUMNS_SELECTOR );
	if ( ! columns ) {
		return null;
	}

	// Must be the first column.
	if ( columns.querySelector( `:scope > ${ COLUMN_SELECTOR }` ) !== column ) {
		return null;
	}

	return column;
}

/**
 * Left edge of the column's content box — where the figure would sit
 * with no bleed applied.
 *
 * We measure the column rather than the figure itself. The figure's own
 * rect reflects the offset we already applied, so reading it means either
 * trusting a stale value or zeroing the variables first and relying on a
 * synchronous style flush. WebKit does not reliably perform that flush,
 * which is what made the image snap between two widths on every frame.
 * The column is never mutated by this script, so its rect is always clean.
 *
 * @param {Element} column
 * @return {number}
 */
function getColumnContentLeft( column ) {
	const styles = getComputedStyle( column );

	return (
		column.getBoundingClientRect().left +
		( parseFloat( styles.borderLeftWidth ) || 0 ) +
		( parseFloat( styles.paddingLeft ) || 0 )
	);
}

function getColumnContentWidth( column ) {
	const styles = getComputedStyle( column );

	return (
		column.getBoundingClientRect().width -
		( parseFloat( styles.borderLeftWidth ) || 0 ) -
		( parseFloat( styles.borderRightWidth ) || 0 ) -
		( parseFloat( styles.paddingLeft ) || 0 ) -
		( parseFloat( styles.paddingRight ) || 0 )
	);
}

// Only touch the DOM when the value actually changes. Every write is a
// mutation, and unguarded rewrites are what kept the observer busy.
function setVariable( el, name, value ) {
	if ( el.style.getPropertyValue( name ) !== value ) {
		el.style.setProperty( name, value );
	}
}

function clearVariables( el ) {
	if ( el.style.getPropertyValue( '--bleed-offset' ) ) {
		el.style.removeProperty( '--bleed-offset' );
	}

	if ( el.style.getPropertyValue( '--bleed-column-width' ) ) {
		el.style.removeProperty( '--bleed-column-width' );
	}
}

function update() {
	const wideWidth = getWideWidth();
	const viewportWidth = document.documentElement.clientWidth;

	// Cap: stop at the wide width boundary.
	const wideLeftEdge = Math.max( 0, ( viewportWidth - wideWidth ) / 2 );

	document.querySelectorAll( FIGURE_SELECTOR ).forEach( ( el ) => {
		const column = getValidColumn( el );

		if ( ! column ) {
			clearVariables( el );

			if ( el.querySelector( 'img[src]:not([src=""])' ) ) {
				console.warn( CONTEXT_WARNING, el );
			}

			return;
		}

		if ( resizeObserver && ! observedColumns.has( column ) ) {
			observedColumns.add( column );
			resizeObserver.observe( column );
		}

		const columnWidth = getColumnContentWidth( column );

		// Hidden, collapsed, or not yet laid out — measuring now would
		// write a nonsense offset that we would then have to undo.
		if ( columnWidth <= 0 ) {
			return;
		}

		// Rounded to whole pixels. Sub-pixel values differ in their final
		// bits between layout passes, so an unrounded comparison never
		// settles and the element is rewritten on every frame forever.
		const offset = -Math.round( getColumnContentLeft( column ) - wideLeftEdge );

		setVariable( el, '--bleed-offset', `${ offset }px` );
		setVariable( el, '--bleed-column-width', `${ Math.round( columnWidth ) }px` );
	} );
}

// Coalesce bursts of mutations into a single measurement per frame.
function scheduleUpdate() {
	if ( pendingFrame ) {
		return;
	}

	pendingFrame = window.requestAnimationFrame( () => {
		pendingFrame = null;
		update();
	} );
}

function initBleedLeft() {
	if ( 'ResizeObserver' in window ) {
		resizeObserver = new ResizeObserver( scheduleUpdate );
	}

	update();

	window.addEventListener( 'resize', scheduleUpdate, { passive: true } );

	// In the editor, blocks re-render dynamically, so watch for structural
	// changes and for style variations being switched on a block.
	//
	// Deliberately NOT watching `style`: this script writes inline styles,
	// so observing them made the callback retrigger itself without end, and
	// it also fired on every inline style the sliders write while animating.
	// Column size changes are handled by the ResizeObserver above.
	new MutationObserver( scheduleUpdate ).observe( document.body, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: [ 'class' ],
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initBleedLeft );
} else {
	initBleedLeft();
}
